// layout.ts — the encounter mini-map's layout and player filter, owned by the browser (owner
// 2026-09-10). Pure functions, no DOM: the store feeds them whatever graph this viewer holds.
//
// Layout is the former server algorithm (options/api/encounter_minimap_inc.php, ft_minimap_layout),
// ported so a GM's first view keeps the shape GMs already know, plus one addition: POSITION MEMORY.
// A player's graph grows one reveal at a time, and re-laying out a grown graph from scratch reshuffles
// every room. Remembered positions anchor the nodes the viewer has already seen, so a reveal only places
// the new rooms around them.
//
// Spring length ≈ 1.0 abstract unit; minimap.ts projects units to pixels.

export interface LayoutPoint {
    x: number;
    y: number;
}

export interface LayoutEdge {
    from: string;
    to: string;
}

/** A location-map marker position; `frame` names the map (coordinates only compare within one map). */
export interface LayoutSeed extends LayoutPoint {
    frame?: string;
}

const CRC_TABLE: Uint32Array = (() => {
    const table = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
        table[n] = c >>> 0;
    }
    return table;
})();

/** PHP crc32() over the string's UTF-8 bytes (UUIDs are ASCII, so bytes = chars). */
export function crc32(text: string): number {
    let crc = 0xffffffff;
    const bytes = new TextEncoder().encode(text);
    for (let i = 0; i < bytes.length; i++) {
        crc = (CRC_TABLE[(crc ^ (bytes[i] ?? 0)) & 0xff] ?? 0) ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}

/** Deterministic 0..1 per-node jitter from the UUID. */
function hash01(id: string): number {
    return (crc32(id) & 0xffffff) / 0xffffff;
}

/** Byte-order compare, matching PHP sort(SORT_STRING) for ASCII ids. */
function cmp(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Deterministic abstract layout.
 *
 * 1. Anchors: a component with ANY remembered position is anchored by memory alone (memory already
 *    absorbed the marker shape when it was made). Otherwise marker `seeds` are rescaled so the average
 *    seeded-edge length ≈ 1.4 and anchor the component.
 * 2. Radial BFS skeleton from the component root (an anchored node, else the smallest UUID); children
 *    fan around their parent at golden-angle offsets jittered by a UUID hash.
 * 3. 160 synchronous spring/repulsion ticks with linear cooling; anchored nodes are pulled 75% back
 *    toward their anchor every tick.
 * 4. Components are normalized and tiled left→right with a 2.0 gap.
 *
 * Same input → same output.
 */
export function layoutMinimap(
    nodeIds: readonly string[],
    edges: readonly LayoutEdge[],
    seeds: Readonly<Record<string, LayoutSeed>> = {},
    memory: Readonly<Record<string, LayoutPoint>> = {},
): Record<string, LayoutPoint> {
    if (nodeIds.length === 0) return {};
    const ids = Array.from(new Set(nodeIds)).sort(cmp);

    const adj: Record<string, string[]> = {};
    for (const id of ids) adj[id] = [];
    for (const e of edges) {
        const af = adj[e.from];
        const at = adj[e.to];
        if (af && at) {
            af.push(e.to);
            at.push(e.from);
        }
    }
    for (const id of ids) adj[id] = Array.from(new Set(adj[id])).sort(cmp);

    // Connected components, each sorted.
    const assigned = new Set<string>();
    const components: string[][] = [];
    for (const id of ids) {
        if (assigned.has(id)) continue;
        const comp: string[] = [];
        const queue: string[] = [id];
        assigned.add(id);
        for (let qi = 0; qi < queue.length; qi++) {
            const cur = queue[qi] as string;
            comp.push(cur);
            for (const nb of adj[cur] ?? []) {
                if (!assigned.has(nb)) {
                    assigned.add(nb);
                    queue.push(nb);
                }
            }
        }
        comp.sort(cmp);
        components.push(comp);
    }

    // Marker anchors. Seeds WITHOUT a frame are one map — the server's old behaviour, a global rescale so
    // the average seeded-edge length ≈ 1.4 (kept exact for parity). Seeds WITH frames come from each room's
    // own location map; map coordinates only compare within a map, so each component is anchored by the
    // frame most of its seeded rooms share, rescaled on that frame's own edges.
    const markerAnchors: Record<string, LayoutPoint> = {};
    const anchorSet = (seedIds: readonly string[]): void => {
        if (seedIds.length >= 2) {
            const inSet = new Set(seedIds);
            const dists: number[] = [];
            for (const e of edges) {
                const a = seeds[e.from];
                const b = seeds[e.to];
                if (a && b && inSet.has(e.from) && inSet.has(e.to)) {
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const d = Math.sqrt(dx * dx + dy * dy);
                    if (d > 0.000001) dists.push(d);
                }
            }
            const avg = dists.length > 0 ? dists.reduce((s, d) => s + d, 0) / dists.length : 0;
            const scale = avg > 0.000001 ? 1.4 / avg : 1.0;
            for (const id of seedIds) {
                const p = seeds[id];
                if (p) markerAnchors[id] = { x: p.x * scale, y: p.y * scale };
            }
        } else if (seedIds.length === 1) {
            const only = seedIds[0];
            if (only !== undefined) markerAnchors[only] = { x: 0, y: 0 };
        }
    };
    const framed = Object.values(seeds).some((s) => s.frame !== undefined);
    if (!framed) {
        anchorSet(Object.keys(seeds).filter((id) => adj[id] !== undefined));
    } else {
        for (const comp of components) {
            const counts = new Map<string, number>();
            for (const id of comp) {
                const f = seeds[id]?.frame;
                if (f !== undefined) counts.set(f, (counts.get(f) ?? 0) + 1);
            }
            let best = '';
            let bestN = 0;
            for (const [f, n] of [...counts].sort(([a], [b]) => cmp(a, b))) {
                if (n > bestN) {
                    best = f;
                    bestN = n;
                }
            }
            if (bestN > 0) anchorSet(comp.filter((id) => seeds[id]?.frame === best));
        }
    }

    const golden = Math.PI * (3.0 - Math.sqrt(5.0));
    const positions: Record<string, LayoutPoint> = {};
    const laid: Array<{ comp: string[]; pos: Record<string, LayoutPoint>; remembered: boolean }> = [];

    for (const comp of components) {
        const remembered = comp.some((id) => memory[id] !== undefined);
        const anchors: Record<string, LayoutPoint> = {};
        for (const id of comp) {
            const p = remembered ? memory[id] : markerAnchors[id];
            if (p) anchors[id] = { x: p.x, y: p.y };
        }

        // Every room already remembered: nothing moves, so there is nothing to simulate.
        if (remembered && comp.every((id) => anchors[id] !== undefined)) {
            const pinned: Record<string, LayoutPoint> = {};
            for (const id of comp) {
                const a = anchors[id] as LayoutPoint;
                pinned[id] = { x: a.x, y: a.y };
            }
            laid.push({ comp, pos: pinned, remembered });
            continue;
        }

        const pos: Record<string, LayoutPoint> = {};
        let root = comp[0] as string;
        for (const cid of comp) {
            if (anchors[cid]) {
                root = cid;
                break;
            }
        }

        const rootAnchor = anchors[root];
        pos[root] = rootAnchor ? { x: rootAnchor.x, y: rootAnchor.y } : { x: 0, y: 0 };
        const bfs: string[] = [root];
        const seen = new Set<string>([root]);
        for (let qi = 0; qi < bfs.length; qi++) {
            const cur = bfs[qi] as string;
            const children: string[] = [];
            for (const nb of adj[cur] ?? []) {
                if (!seen.has(nb)) {
                    seen.add(nb);
                    children.push(nb);
                    bfs.push(nb);
                }
            }
            const n = children.length;
            const parent = pos[cur] as LayoutPoint;
            children.forEach((child, i) => {
                const anchor = anchors[child];
                if (anchor) {
                    pos[child] = { x: anchor.x, y: anchor.y };
                    return;
                }
                const angle = hash01(cur) * 2.0 * Math.PI
                    + (i - (n - 1) / 2.0) * golden * 0.5
                    + (hash01(child) - 0.5) * 0.9;
                pos[child] = { x: parent.x + Math.cos(angle), y: parent.y + Math.sin(angle) };
            });
        }

        const count = comp.length;
        // 160 ticks, as the server ran them; very large webs get fewer (every tick is O(n²)).
        const iters = count > 1 ? (count > 300 ? Math.max(40, Math.round((160 * 300) / count)) : 160) : 0;
        // In a remembered web only the NEW rooms move: pinned↔pinned work is skipped and pinned rooms take
        // no force. Without memory every room moves — the server's exact arithmetic (parity).
        const moverSet = remembered ? new Set(comp.filter((id) => anchors[id] === undefined)) : null;
        const moves = (id: string): boolean => moverSet === null || moverSet.has(id);
        for (let it = 0; it < iters; it++) {
            const cool = 1.0 - it / iters;
            const force: Record<string, LayoutPoint> = {};
            for (const id of comp) force[id] = { x: 0, y: 0 };

            for (let i = 0; i < count; i++) {
                for (let j = i + 1; j < count; j++) {
                    const a = comp[i] as string;
                    const b = comp[j] as string;
                    if (moverSet !== null && !moverSet.has(a) && !moverSet.has(b)) continue;
                    const pa = pos[a] as LayoutPoint;
                    const pb = pos[b] as LayoutPoint;
                    let dx = pa.x - pb.x;
                    let dy = pa.y - pb.y;
                    let d2 = dx * dx + dy * dy;
                    if (d2 < 0.0001) {
                        dx = (hash01(a) - 0.5) * 0.01;
                        dy = (hash01(b) - 0.5) * 0.01;
                        d2 = dx * dx + dy * dy + 0.0001;
                    }
                    const d = Math.sqrt(d2);
                    const f = Math.min(0.35 / d2, 2.0);
                    const fa = force[a] as LayoutPoint;
                    const fb = force[b] as LayoutPoint;
                    if (moves(a)) {
                        fa.x += (dx / d) * f;
                        fa.y += (dy / d) * f;
                    }
                    if (moves(b)) {
                        fb.x -= (dx / d) * f;
                        fb.y -= (dy / d) * f;
                    }
                }
            }
            for (const e of edges) {
                const pa = pos[e.from];
                const pb = pos[e.to];
                if (!pa || !pb) continue;
                if (moverSet !== null && !moverSet.has(e.from) && !moverSet.has(e.to)) continue;
                const dx = pb.x - pa.x;
                const dy = pb.y - pa.y;
                const d = Math.sqrt(dx * dx + dy * dy);
                if (d < 0.000001) continue;
                const f = (d - 1.0) * 0.18;
                const fa = force[e.from] as LayoutPoint;
                const fb = force[e.to] as LayoutPoint;
                if (moves(e.from)) {
                    fa.x += (dx / d) * f;
                    fa.y += (dy / d) * f;
                }
                if (moves(e.to)) {
                    fb.x -= (dx / d) * f;
                    fb.y -= (dy / d) * f;
                }
            }
            // Marker anchors pull 75% (the shape leads, the web breathes); remembered rooms are PINNED —
            // the viewer already saw them there.
            const pull = remembered ? 1.0 : 0.75;
            const step = 0.12 * cool + 0.02;
            for (const id of comp) {
                const p = pos[id] as LayoutPoint;
                const f = force[id] as LayoutPoint;
                p.x += f.x * step;
                p.y += f.y * step;
                const anchor = anchors[id];
                if (anchor) {
                    p.x = p.x * (1 - pull) + anchor.x * pull;
                    p.y = p.y * (1 - pull) + anchor.y * pull;
                }
            }
        }
        laid.push({ comp, pos, remembered });
    }

    // Remembered components keep their own coordinate frame (no re-normalizing, so nothing the viewer
    // has seen slides); every other component is normalized and tiled left→right after them, 2.0 apart.
    // With no memory this is exactly the server's tiling.
    let offsetX = 0;
    let anyRemembered = false;
    let rememberedMaxX = -Infinity;
    for (const { comp, pos, remembered } of laid) {
        if (!remembered) continue;
        anyRemembered = true;
        for (const id of comp) {
            const p = pos[id] as LayoutPoint;
            positions[id] = { x: p.x, y: p.y };
            rememberedMaxX = Math.max(rememberedMaxX, p.x);
        }
    }
    if (anyRemembered) offsetX = rememberedMaxX + 2.0;
    for (const { comp, pos, remembered } of laid) {
        if (remembered) continue;
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        for (const id of comp) {
            const p = pos[id] as LayoutPoint;
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minY = Math.min(minY, p.y);
        }
        for (const id of comp) {
            const p = pos[id] as LayoutPoint;
            positions[id] = { x: p.x - minX + offsetX, y: p.y - minY };
        }
        offsetX += maxX - minX + 2.0;
    }

    return positions;
}

/** PHP round($v, 2) — half away from zero. */
export function roundLayoutCoordinate(value: number): number {
    return (Math.sign(value) * Math.round(Math.abs(value) * 100)) / 100;
}

/** Name-compatible with the PHP oracle ft_minimap_layout() (no memory) — the parity test's entry. */
export function minimapLayout(
    nodeIds: readonly string[],
    edges: readonly LayoutEdge[],
    seedPos: Readonly<Record<string, LayoutPoint>> | readonly unknown[] = {},
): Record<string, LayoutPoint> {
    // PHP json_encode writes an empty seed map as [] — treat any array as "no seeds".
    const seeds = Array.isArray(seedPos) ? {} : (seedPos as Readonly<Record<string, LayoutPoint>>);
    return layoutMinimap(nodeIds, edges, seeds);
}

/**
 * Name- and shape-compatible with the PHP oracle ft_minimap_player_filter(): nodes carry `explored`
 * as 0/1 and `current`; surviving edges keep `private` (always false); `reason` is 'nothing_explored'
 * when only the current room is left with no visible connection.
 */
export function minimapPlayerFilter<
    N extends { id: string; name: string; explored: number; current: boolean; frontier: boolean },
    E extends FilterEdge,
>(nodes: readonly N[], edges: readonly E[]): { nodes: N[]; edges: E[]; reason: string } {
    const visible = new Set<string>();
    for (const n of nodes) if (n.explored === 1 || n.current) visible.add(n.id);
    const frontier = new Set<string>();
    for (const e of edges) {
        if (e.private) continue;
        if (visible.has(e.from) && !visible.has(e.to)) frontier.add(e.to);
        else if (visible.has(e.to) && !visible.has(e.from)) frontier.add(e.from);
    }
    const outEdges = edges.filter((e) => {
        if (e.private) return false;
        const aOk = visible.has(e.from) || frontier.has(e.from);
        const bOk = visible.has(e.to) || frontier.has(e.to);
        return aOk && bOk && (visible.has(e.from) || visible.has(e.to));
    });
    const outNodes: N[] = [];
    for (const n of nodes) {
        if (visible.has(n.id)) outNodes.push(n);
        else if (frontier.has(n.id)) outNodes.push({ ...n, name: '', frontier: true });
    }
    const reason = outNodes.length <= 1 && outEdges.length === 0 ? 'nothing_explored' : '';
    return { nodes: outNodes, edges: outEdges, reason };
}

export interface FilterNode {
    id: string;
    name: string;
    explored: boolean;
}

export interface FilterEdge extends LayoutEdge {
    private: boolean;
}

/**
 * The player perspective of a graph the viewer already holds (a GM looking through a PC's eyes): explored
 * rooms + the current room, unexplored neighbours via public edges as anonymized frontier stubs; private
 * and frontier↔frontier edges dropped. Same rule the server applies before a player's snapshot leaves it.
 */
export function projectPlayerView<N extends FilterNode, E extends FilterEdge>(
    nodes: readonly N[],
    edges: readonly E[],
    currentId: string,
): { nodes: Array<N & { frontier: boolean }>; edges: E[] } {
    const visible = new Set<string>();
    for (const n of nodes) if (n.explored || n.id === currentId) visible.add(n.id);
    const frontier = new Set<string>();
    for (const e of edges) {
        if (e.private) continue;
        if (visible.has(e.from) && !visible.has(e.to)) frontier.add(e.to);
        else if (visible.has(e.to) && !visible.has(e.from)) frontier.add(e.from);
    }
    const outEdges = edges.filter((e) => {
        if (e.private) return false;
        const aOk = visible.has(e.from) || frontier.has(e.from);
        const bOk = visible.has(e.to) || frontier.has(e.to);
        return aOk && bOk && (visible.has(e.from) || visible.has(e.to));
    });
    const outNodes: Array<N & { frontier: boolean }> = [];
    for (const n of nodes) {
        if (visible.has(n.id)) outNodes.push({ ...n, frontier: false });
        else if (frontier.has(n.id)) outNodes.push({ ...n, name: '', frontier: true });
    }
    return { nodes: outNodes, edges: outEdges };
}
